import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { toHashRouterUrl } from 'components/Pages/StudyReport/studyReportViewerLinks';
import { FlowClassName } from 'interfaces/experimentResults';
import {
  CalculateFeaturesManifestSubTask,
  InferenceManifest,
  Job,
  JobStatus,
  JobType,
  SlideRegistrationsManifest,
} from 'interfaces/job';
import {
  filter,
  flatMap,
  fromPairs,
  groupBy,
  includes,
  isArray,
  isEmpty,
  isString,
  keyBy,
  map,
  reduce,
  size,
  toPairs,
  uniq,
  values,
} from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useClipboard } from 'use-clipboard-copy';
import { downloadDataAsCSV } from 'utils/exports';
import { useUiSettings } from 'utils/queryHooks/uiConstantsHooks';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { getUrlToDatasetTabForSlideIds } from 'utils/useNavigationToDatasetTab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { getJobStatusDisplayLabel, JobStatusChip } from './JobStatusChip';
import { RelatedJobsTable } from './RelatedJobsTable';

const SKIP = 'skip';
const UNKNOWN = 'unknown';

interface SubtaskDetailsProps {
  job: Partial<Job>;
  isLoading?: boolean;
}

interface SlideStatusEntry {
  slideId: string;
  status?: string;
}

const getDefaultManifestStatus = (job: Partial<Job>) => {
  const isDirectNonParentJob = job.isParentJob === false && !job.parentId;
  return isDirectNonParentJob ? job.status || UNKNOWN : UNKNOWN;
};

const getSlideRegistrationKey = (registrationSlides: { slide_id1: string; slide_id2: string }) =>
  `${registrationSlides.slide_id1}:${registrationSlides.slide_id2}`;

interface ManifestSlideRow {
  label: string;
  slideId: string;
}

const getManifestSlideRows = (manifest: unknown): ManifestSlideRow[] =>
  flatMap(isArray(manifest) ? manifest : [], (entry) => {
    if (isString(entry)) {
      return [{ label: entry, slideId: entry }];
    }

    if (!entry || typeof entry !== 'object' || !('case_id' in entry) || !('slides' in entry)) {
      return [];
    }

    const { case_id: caseId, slides } = entry as {
      case_id: number | string;
      slides: Record<string, unknown>;
    };

    return map(filter(values(slides), isString), (slideId) => ({
      label: `${caseId} | ${slideId}`,
      slideId,
    }));
  });

export const SubtaskDetails: React.FC<SubtaskDetailsProps> = ({ job, isLoading = false }) => {
  const { labId } = useCurrentLabId();
  const slideClipboard = useClipboard({ copiedTimeout: 1000 });
  const hasRelatedJobs = !isEmpty(job?.children);

  const { uiSettings } = useUiSettings();
  const jobManifest = job?.manifest;
  const { slideIds, subTasks, subTasksStatusBySlide, lengthSubTasksByStatus, slideStatusRows, slideIdsByStatus } =
    React.useMemo(() => {
      const memoizedSlideIds =
        job?.type === JobType.PrepareCustomerResults
          ? []
          : job?.type === JobType.PostProcessingAndCalculateFeatures
          ? flatMap(job?.manifest)
          : job?.manifest;
      const manifestSlideRows = getManifestSlideRows(memoizedSlideIds);

      const memoizedSubTasks = ((job?.children ?? []) as Job[]).filter(
        (childrenJob) =>
          // Calculate-features subtask (per-case) children are not reliably tagged with their own
          // type or flowClassName in production - but every non-flowClassName-tagged child of a
          // calculate_features parent is a calculate_features subtask, so key off the parent's type.
          job?.type === JobType.PostProcessingAndCalculateFeatures ||
          (childrenJob?.flowClassName &&
            includes(
              [
                FlowClassName.ConcurrentInferenceFlow,
                FlowClassName.CalculateFeatures,
                FlowClassName.PerformRegistrationFlow,
                FlowClassName.FlexibleInferenceFlow,
              ],
              childrenJob.flowClassName
            ))
      );

      const subTaskStatusEntries = memoizedSubTasks.reduce<SlideStatusEntry[]>((entries, inferenceFlow) => {
        if (
          inferenceFlow?.flowClassName === FlowClassName.ConcurrentInferenceFlow ||
          inferenceFlow?.flowClassName === FlowClassName.FlexibleInferenceFlow
        ) {
          entries.push(
            ...map(inferenceFlow?.manifest as InferenceManifest, (slide) => ({
              slideId: isString(slide) ? slide : slide?.slide_id,
              status: inferenceFlow?.status,
            }))
          );
          return entries;
        }

        if (job?.type === JobType.PostProcessingAndCalculateFeatures) {
          const manifest = inferenceFlow?.manifest as CalculateFeaturesManifestSubTask;
          const manifestSlides = flatMap(manifest, (entry) => flatMap(values(entry)));
          entries.push(
            ...map(manifestSlides, (slideId: string) => ({
              slideId,
              status: inferenceFlow?.status,
            }))
          );
          return entries;
        }

        if (inferenceFlow?.flowClassName === FlowClassName.PerformRegistrationFlow) {
          entries.push(
            ...map(
              inferenceFlow?.manifest as SlideRegistrationsManifest,
              (registrationSlides: { slide_id1: string; slide_id2: string }) => ({
                slideId: getSlideRegistrationKey(registrationSlides),
                status: inferenceFlow?.status,
              })
            )
          );
          return entries;
        }

        return entries;
      }, []);

      const memoizedSubTasksStatusBySlide = keyBy(subTaskStatusEntries, 'slideId');
      const memoizedLengthSubTasksByStatus = fromPairs(
        map(toPairs(groupBy(memoizedSubTasks, 'status')), ([key, value]) => [key, value.length])
      );

      const getResolvedSlideStatus = (slideKey: string, defaultStatus: string) =>
        memoizedSubTasksStatusBySlide[slideKey]?.status || defaultStatus;

      const defaultSubTaskStatusByJobStatus = getDefaultManifestStatus(job);

      const memoizedSlideStatusRows: { key: string; label: string; slideIds: string[]; status: string }[] =
        job?.type === JobType.SlideRegistrations
          ? map(memoizedSlideIds as { slide_id1: string; slide_id2: string }[], (registrationSlides) => {
              const key = getSlideRegistrationKey(registrationSlides);
              return {
                key,
                label: key,
                slideIds: [registrationSlides.slide_id1, registrationSlides.slide_id2],
                status: getResolvedSlideStatus(key, defaultSubTaskStatusByJobStatus),
              };
            })
          : map(manifestSlideRows, ({ label, slideId }) => ({
              key: label,
              label,
              slideIds: [slideId],
              status: getResolvedSlideStatus(slideId, defaultSubTaskStatusByJobStatus),
            }));

      const memoizedSlideIdsByStatus = reduce(
        memoizedSlideStatusRows,
        (acc: Record<string, string[]>, row) => {
          acc[row.status] = uniq([...(acc[row.status] || []), ...row.slideIds]);
          return acc;
        },
        {}
      );

      return {
        slideIds: memoizedSlideIds,
        subTasks: memoizedSubTasks,
        subTasksStatusBySlide: memoizedSubTasksStatusBySlide,
        lengthSubTasksByStatus: memoizedLengthSubTasksByStatus,
        slideStatusRows: memoizedSlideStatusRows,
        slideIdsByStatus: memoizedSlideIdsByStatus,
      };
    }, [job?.children, job?.manifest, job?.status, job?.type]);

  const defaultSubTaskStatusByJobStatus = getDefaultManifestStatus(job);

  if (size(subTasks) < size(jobManifest)) {
    const difference = size(jobManifest) - size(subTasks);

    lengthSubTasksByStatus[defaultSubTaskStatusByJobStatus] =
      (lengthSubTasksByStatus[defaultSubTaskStatusByJobStatus] ?? 0) + difference;
  }

  const jobStatusToDisplayText = fromPairs(
    map(uiSettings?.enumDisplayNames?.['jobStatus'], ({ value, label }) => [value, label]) || []
  );
  jobStatusToDisplayText[SKIP] = 'Skipped';
  const getSubtaskStatusLabel = (status?: string) => getJobStatusDisplayLabel(status, jobStatusToDisplayText);

  const { getUrlToSlidePage } = useNavigationToViewerPage();

  const getResolvedSlideStatus = (slideKey: string) =>
    subTasksStatusBySlide[slideKey]?.status || defaultSubTaskStatusByJobStatus;

  const failedSlideIds = slideIdsByStatus[JobStatus.Failed] || [];
  const hasSlideRows = !isEmpty(slideStatusRows);

  const handleCopySlideIdsForStatus = (status: string) => {
    slideClipboard.copy((slideIdsByStatus[status] || []).join('\n'));
  };

  const handleExportSlidesCsv = () => {
    const csvRows: string[][] = [
      ['Slide Id', 'Status', 'App Link'],
      ...flatMap(slideStatusRows, (row) =>
        map(row.slideIds, (slideId) => [
          slideId,
          jobStatusToDisplayText[row.status] || row.status,
          `${window.location.origin}${toHashRouterUrl(
            getUrlToSlidePage({ slideId, labId, caseStudyId: job?.studyId })
          )}`,
        ])
      ),
    ];

    downloadDataAsCSV(csvRows, `job-${job?.id || 'subtasks'}-slides`);
  };

  return (
    <Grid item>
      {isLoading ? (
        <Skeleton variant="rectangular" height={150} />
      ) : (
        (!isEmpty(job?.manifest) || hasRelatedJobs) && (
          <Accordion defaultExpanded slotProps={{ transition: { unmountOnExit: true } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="h4">Sub Tasks</Typography>
                </Grid>
                <Grid item container spacing={1} width="auto" alignItems="center">
                  {!isEmpty(failedSlideIds) && job?.studyId && (
                    <Grid item>
                      <Link
                        to={getUrlToDatasetTabForSlideIds({
                          studyId: job.studyId,
                          labId,
                          slideIds: failedSlideIds,
                          statuses: [JobStatus.Failed],
                        })}
                        component={RouterLink}
                        underline="hover"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      >
                        <Typography variant="body2">
                          View {failedSlideIds.length} failed slide{failedSlideIds.length !== 1 ? 's' : ''} in Dataset
                        </Typography>
                      </Link>
                    </Grid>
                  )}
                  {hasSlideRows && (
                    <Grid item>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon fontSize="small" />}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleExportSlidesCsv();
                        }}
                      >
                        Export CSV
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid item container direction="column" spacing={1}>
                {hasRelatedJobs && (
                  <>
                    <RelatedJobsTable
                      childJobs={(job?.children ?? []) as Job[]}
                      getSubtaskStatusLabel={getSubtaskStatusLabel}
                    />
                    {hasSlideRows && (
                      <Grid item>
                        <Divider />
                      </Grid>
                    )}
                  </>
                )}
                {hasSlideRows && (
                  <>
                    <Grid item container direction="column">
                      {map(toPairs(lengthSubTasksByStatus), ([key, value]) => {
                        return (
                          <Grid
                            item
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            key={`subtask-status-${key}`}
                          >
                            <Grid item>
                              <Typography variant="h5">{value}</Typography>
                            </Grid>
                            <Grid item container spacing={0.5} width="auto" alignItems="center">
                              <Grid item>
                                <JobStatusChip status={key} label={getSubtaskStatusLabel(key)} />
                              </Grid>
                              <Grid item>
                                <Tooltip
                                  title={
                                    slideClipboard.copied
                                      ? 'Copied!'
                                      : `Copy ${value} slide ID${value !== 1 ? 's' : ''}`
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    aria-label={`Copy ${getSubtaskStatusLabel(key)} slide IDs`}
                                    onClick={() => handleCopySlideIdsForStatus(key)}
                                  >
                                    <ContentCopyIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </Grid>
                            </Grid>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Grid item>
                      <Divider />
                    </Grid>
                    <Grid item sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', columnGap: 2 }}>
                      <Grid item sx={{ minWidth: 0 }}>
                        <Typography variant="h4">Slide Id</Typography>
                      </Grid>
                      <Grid item sx={{ flexShrink: 0 }}>
                        <Typography variant="h4">Status</Typography>
                      </Grid>
                    </Grid>
                    {job?.type === JobType.SlideRegistrations
                      ? map(slideIds, (registrationSlides: { slide_id1: string; slide_id2: string }) => {
                          return (
                            <Grid
                              item
                              alignItems="center"
                              key={getSlideRegistrationKey(registrationSlides)}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) auto',
                                columnGap: 2,
                              }}
                            >
                              <Grid item sx={{ minWidth: 0 }}>
                                <Link
                                  to={getUrlToSlidePage({
                                    slideId: registrationSlides.slide_id1,
                                    labId,
                                    caseStudyId: job?.studyId,
                                  })}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  component={RouterLink}
                                  sx={{ display: 'block', overflowWrap: 'anywhere' }}
                                >
                                  <Typography variant="h5">{`${registrationSlides.slide_id1}`}</Typography>
                                </Link>
                                <Link
                                  to={getUrlToSlidePage({
                                    slideId: registrationSlides.slide_id2,
                                    labId,
                                    caseStudyId: job?.studyId,
                                  })}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  component={RouterLink}
                                  sx={{ display: 'block', overflowWrap: 'anywhere' }}
                                >
                                  <Typography variant="h5">{`${registrationSlides.slide_id2}`}</Typography>
                                </Link>
                              </Grid>
                              <Grid item sx={{ flexShrink: 0 }}>
                                <JobStatusChip
                                  status={getResolvedSlideStatus(getSlideRegistrationKey(registrationSlides))}
                                  label={getSubtaskStatusLabel(
                                    getResolvedSlideStatus(getSlideRegistrationKey(registrationSlides))
                                  )}
                                />
                              </Grid>
                            </Grid>
                          );
                        })
                      : map(slideStatusRows, ({ key, label, slideIds: [slideId], status }) => {
                          return (
                            <Grid
                              item
                              alignItems="center"
                              key={`subtask-slide-${key}`}
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) auto',
                                columnGap: 2,
                              }}
                            >
                              <Grid item sx={{ minWidth: 0 }}>
                                <Link
                                  to={getUrlToSlidePage({
                                    slideId,
                                    labId,
                                    caseStudyId: job?.studyId,
                                  })}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  component={RouterLink}
                                  sx={{ display: 'block', overflowWrap: 'anywhere' }}
                                >
                                  <Typography variant="h5">{label}</Typography>
                                </Link>
                              </Grid>
                              <Grid item sx={{ flexShrink: 0 }}>
                                <JobStatusChip status={status} label={getSubtaskStatusLabel(status)} />
                              </Grid>
                            </Grid>
                          );
                        })}
                  </>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )
      )}
    </Grid>
  );
};
